/**
 * The views inside a destination (D-322, Rule 3).
 *
 * **A second-level row may only exist inside a rail item, as views of that one
 * thing.** That is the rule; this is the list, and it is data for the same
 * reason the destinations are: one place decides what belongs where, and a
 * screen cannot quietly grow a nav of its own.
 *
 * **It also stopped seven screens being orphaned.** Retiring the header menus
 * took the only link to Staff & access, Import, Classroom tablets, Message
 * templates, Dancers, Long bookings and Conflicts with them — every one still
 * rendered perfectly when visited directly, so nothing failed and nothing in the
 * layout audit could see it. A rail carrying nine destinations does not replace
 * a header carrying sixteen links; it replaces the top level of them.
 */
export type NavView = {
  /** What the tab says. Short, because it sits beside its siblings. */
  label: string

  /**
   * What the screen says, when a tab is too narrow to say it (D-464).
   *
   * A tab and a heading are read in different places and have different room.
   * *Substitutes* is right beside *All classes*; *Classes needing a substitute*
   * is right above the list it describes and tells somebody who has never
   * opened the screen what they are looking at. Defaults to `label`, which is
   * correct for every other view.
   */
  heading?: string
  to: string

  /** Any one of these is enough, as on a destination. Empty means ungated. */
  needsAny?: readonly string[]

  /**
   * Offered only to an account that **both** teaches and is a parent.
   *
   * **Named for the conjunction rather than spelling it as two flags, and that
   * is the whole point.** `Destination` already has `needsTeaching` and
   * `needsParent`, and there they mean *any one of these is enough* — a
   * destination asks *may she come here at all*. A view bar asks *is there
   * anything to choose between*, and a choice needs both sides to exist.
   * Reusing those two names here would put the same fields, with the same
   * values, under the same destination key, two files apart, meaning the
   * opposite thing; the obvious later tidy — *"these share four flags, let us
   * share the predicate"* — would silently invert one of them, and it fails
   * quietly in the direction that matters: a missing rail item is loud, a
   * *My children* tab for somebody who has no children is not.
   *
   * Without it, a teacher who is nobody's parent gets a row reading *My
   * classes* with nothing to switch to — the control-that-changes-nothing
   * D-257 removed, rebuilt one level down.
   */
  onlyWhenBoth?: boolean

  /**
   * These views read the same anchor out of the address, so moving between them
   * carries it (D-360).
   *
   * **Without it the feature stops at the link.** Both halves of Schedule read
   * `?week=` and `?view=`, and `ViewBar` is the only control in the app that
   * moves a dual-role reader between them — but `<NavLink to="/schedule/dancers">`
   * resolves a bare pathname with an empty search, so paging the teaching week
   * to October and tapping *My children* landed on this week. Every half read
   * the parameter correctly and nothing ever supplied it.
   *
   * **Only the parameters both sides mean the same thing by.** `?dancer=` is
   * deliberately not carried: it names something one half has and the other
   * does not, and an address claiming she is looking at a child on a screen
   * that shows none is worse than dropping it (D-329).
   */
  sharesAnchor?: boolean

  /** Matched exactly rather than as a prefix. `/` would otherwise match all. */
  end?: boolean
}

/**
 * Keyed by destination.
 *
 * A destination with no entry has no second-level row, which is the common
 * case and the one to prefer: Rule 3 says the row is *earned*.
 *
 * **Calendar had none, and the reversal is the information** (D-324, amended
 * by D-560). Clashes and long bookings were listed here first and taken out:
 * neither is a *place*, each is something that happened and needs a decision,
 * so they surfaced as alerts that vanish when there is nothing to act on. That
 * argument still holds for the alert. What it could not survive is the alert
 * being the *only* door: it counts this week and disappears at zero, so a
 * conflict a fortnight out had nothing pointing at it once the button beside
 * Day/Week/Month went. So the conflict list is a view of the calendar, which
 * is what it always was - a way of reading the timetable - while the studio's
 * *problems* stay off the rail, where D-324 put them.
 */
export const VIEWS: Record<string, readonly NavView[]> = {
  /*
   * A teacher who is also a parent, given her two lives one tap apart (D-352).
   *
   * **Measured, not assumed.** On a real dual-role account at 390px her
   * children's half sat 2,184px down — 2.6 screens — behind her own week and a
   * cover panel. Moving the cover panel out from between them recovers 23% and
   * leaves it at 2.0 screens, and the week is 1,472px whether it holds twelve
   * classes or none, because a seven-day grid is structural rather than
   * content-sized. Nothing short of separating the halves touches it.
   *
   * **This completes D-326 rather than reversing D-331.** D-326 said in as many
   * words that where a screen differs by reader it *"becomes a view inside one
   * destination, addressable by URL"*. D-331 deleted a switch between whole
   * *portals*, with different capabilities and different rails, and took the
   * per-device stickiness with it. The reversal to watch for is memory, not
   * separation: **a mode is remembered, a view is linked.** Nothing here
   * remembers which half she was on.
   *
   * **`/schedule/dancers`, never `?view=`.** `ParentSchedulePanel` already owns
   * `?view=week|day|month` in this same address bar, and a nav idea sharing a
   * word with a calendar idea at one URL is the collision D-336 removed from
   * Classes.
   *
   * **Both are sub-paths, and the plain `/schedule` is deliberately not one of
   * them.** `DestinationScreen` names its heading after whichever view matches
   * the path, so putting *My classes* on `/schedule` gave a parent who teaches
   * nothing an `<h1>My classes</h1>` over her children's week — she never sees
   * the bar, so nothing on screen would have explained it. A dual-role account
   * is redirected off the plain address to whichever half applies; everyone
   * else stays on it, matches no view, and keeps the heading *Schedule*.
   */
  schedule: [
    { label: 'My classes', to: '/schedule/classes', onlyWhenBoth: true, sharesAnchor: true },
    { label: 'My children', to: '/schedule/dancers', onlyWhenBoth: true, sharesAnchor: true },
  ],

  /*
   * D-322's teacher table, built: *Classes | rosters · To mark · Absences*.
   *
   * **`Classes`, not `My classes`** — owner's call (D-336). Hers are the only
   * ones this destination can show, so the possessive was answering a question
   * nobody reading it had. The qualifier moved to the admin destination, which
   * is where it earns its place: that one really is every class in the studio.
   *
   * **Without this a teacher could see the number and not the list.** Her
   * screen says "3 earlier classes still to mark" as a plain sentence,
   * and the header menu that used to carry the link is gone. The Attendance
   * destination is not the answer: it is gated on `ManageAttendance`, which
   * no teacher holds, because hers is *her own classes* and that one is *the
   * studio's* (D-188, D-118).
   */
  classes: [
    { label: 'Classes', to: '/my-classes', needsAny: ['ViewSchedule'] },
    /*
     * Her own Standing (D-590): the same list the studio's Attendance opens
     * on, scoped to her classes by the server, with a flag where the studio
     * has a send. Driven in a browser, a teacher sent to `/attendance` got
     * "Not your page" - that destination is ManageAttendance, which she does
     * not hold - so the door is here, beside her Attendance and Absences.
     */
    { label: 'Standing', to: '/my-standing', needsAny: ['ViewAttendance'] },
    { label: 'Attendance', to: '/my-attendance', needsAny: ['TakeAttendance'] },
    { label: 'Absences', to: '/absences', needsAny: ['ViewAttendance'] },
    // Her own dancers' banked classes (D-745), scoped by the server the way
    // her Standing is.
    { label: 'Banked', to: '/my-banked', needsAny: ['ViewAttendance'] },
  ],

  // Owner-confirmed: "people for dancers, families and staff makes sense to me".
  people: [
    { label: 'Families', to: '/admin/people', needsAny: ['ManagePeople'] },
    { label: 'Dancers', to: '/admin/dancers', needsAny: ['ManagePeople'] },
    { label: 'Staff & access', to: '/admin/staff', needsAny: ['ManageStaff'] },
  ],

  /*
   * **"Missing", not "Attendance"** (D-389).
   *
   * The word *register* is gone from everything a person reads — it is the
   * paper book a teacher used to sign, and the app calls the thing attendance
   * (D-378). But this destination is *already* called Attendance, and a view
   * with its destination's name is the case `DestinationScreen.test.tsx` warns
   * about by name: with both reading *Attendance*, nothing can tell which one a
   * heading used, and that test quietly stops checking anything.
   *
   * *Missing* is what the screen has always called itself — its heading reads
   * "Missing attendance" — so this is the screen's own word rather than a
   * coinage. The teacher's equivalent view keeps *Attendance*, because her
   * destination is *Classes* and there is nothing to collide with.
   */
  /*
   * **Standing first** (D-590). `asked`: *"it should be the first page that
   * opens when you click attendance... then the absences one, and then
   * missing since missing is really just something tonya needs to get on her
   * teachers to go cleanup."* Missing moved off the destination's own address
   * to make room; nothing linked to it by that address except the rail.
   */
  attendance: [
    { label: 'Standing', to: '/attendance', needsAny: ['ManageAttendance'], end: true },
    { label: 'Absences', to: '/attendance/absences', needsAny: ['ManageAttendance'] },
    { label: 'Missing', to: '/attendance/missing', needsAny: ['ManageAttendance'] },
    /*
     * Banked classes (D-745), last: it is a reference list rather than
     * something owed, and the three before it are the work.
     */
    { label: 'Banked', to: '/attendance/banked', needsAny: ['ManageAttendance'] },
  ],

  /*
   * **Cover is a class question, so it lives with classes** - and on the same
   * capability, `ManageClasses`, which is what the `/uncovered` endpoint
   * already required. Matching it exactly rather than approximately is what
   * stops a view bar offering a row the API refuses.
   *
   * Before this, `class-admin` had no views and the queue had no screen at all:
   * `fetchUncovered` and `askToCover` shipped with their endpoints and were
   * called from nowhere (D-332).
   *
   * **`All classes` is not a synonym for `Classes`, and that is the point** —
   * owner's call (D-336). This screen really is every class in the studio,
   * where a teacher's is only the ones she teaches, so the qualifier carries
   * information rather than padding a label. It was briefly renamed to
   * *Classes* to match the page heading; the heading was the thing that was
   * wrong, and it now reads *All classes* too.
   */
  'class-admin': [
    { label: 'All classes', to: '/admin/classes', needsAny: ['ManageClasses'], end: true },
    /*
     * **It said *Cover*, which named the arrangement and not the thing**
     * (D-464). `asked`: the tab was the confusing part - the rows reading
     * *"Ashley Alley is out. Nobody covering yet"* were fine.
     *
     * **The app already had a word for this.** `SubstituteInstructorId`,
     * `AssignSubstitute`, `SubstituteFor` - and the template families actually
     * receive says *"{{substituteName}} is taking {{className}}"*. Every
     * parent already reads *substitute*; this one screen said *cover*.
     *
     * **Not *Absences*, which is taken.** The portal has *Report an absence*
     * for dancers, and two Absences in one system - one about children, one
     * about staff - is worse than the vagueness it replaced.
     */
    {
      label: 'Substitutes',
      heading: 'Classes needing a substitute',
      to: '/admin/cover',
      needsAny: ['ManageClasses'],
    },
  ],

  /*
   * What is set up once a season and then left alone (D-167) - which is where
   * the import belongs, and where it was before the gear went.
   */
  /*
   * <b>One view each, which means neither draws a bar</b> (D-585).
   *
   * <c>ViewBar</c> hides a row whose only view is the destination's own address
   * (D-257) - a control that changes nothing. So the <i>Calendar | Conflicts</i>
   * tabs are gone and Day/Week/Month, which are their own control in the
   * header, are untouched.
   *
   * <b>The entries stay because paths resolve through them.</b>
   * <c>destinationForPath</c> reads only this map, and <c>DestinationScreen</c>
   * titles a screen from whichever view matches - remove them and both
   * destinations answer <c>null</c>.
   */
  calendar: [{ label: 'Calendar', to: '/calendar', needsAny: ['ViewAllSchedules'] }],
  conflicts: [{ label: 'Conflicts', to: '/conflicts', needsAny: ['ViewAllSchedules'] }],

  /*
   * What is set up once a season and then left alone (D-167) - which is where
   * the import belongs, and where it was before the gear went.
   */
  studio: [
    /*
     * ManageClasses, because that is what this screen's own writes require.
     * (Its studio-dates *read* is ViewSchedule, deliberately - a task can be
     * hung on one of those dates and every member of staff may raise a task -
     * but nobody reaches this row without the rest of the screen.) It asked
     * for ManageRooms and ManageSeasons for months, neither of which gates
     * anything here, so anybody granted only those was offered a link that
     * refused her on arrival; the destination in `nav.ts` carried the same
     * fault and is corrected with it.
     */
    { label: 'Studio setup', to: '/admin/studio', needsAny: ['ManageClasses'] },
    { label: 'Classroom tablets', to: '/admin/devices', needsAny: ['ManageStaff'] },
    { label: 'Import', to: '/admin/import', needsAny: ['ManageIntegrations'], end: true },
    // The other half of the import: what has to go back by hand (D-694).
    // Its own capability, so it can be granted without the studio reset going
    // with it (D-813).
    { label: 'Jackrabbit updates', to: '/admin/carryover', needsAny: ['ManageCarryover'] },
    // The feed's own record, which changes nothing (D-816).
    { label: 'What Jackrabbit sent', to: '/admin/import/activity', needsAny: ['ManageIntegrations'] },
  ],

  /*
   * Her words and her pictures. Message templates sit here rather than under
   * Studio because they are copy a person writes, not a setting she configures -
   * the same reason the public content does.
   */
  website: [
    { label: 'Content', to: '/admin/content', needsAny: ['EditSiteContent'] },
    { label: 'Photos', to: '/admin/media', needsAny: ['ManageMedia'] },
    /*
     * Templates, not Messages (D-625, answering the spec's Q8).
     *
     * This screen edits the wording of the 54 notifications the app sends; the
     * communications module owns the word Messages. Two entries with one label
     * is a navigation that cannot be read aloud, and this is the cheaper side
     * to rename - almost nobody opens it, while everybody will open the other.
     */
    { label: 'Templates', to: '/admin/messages', needsAny: ['ManageNotificationTemplates'] },
  ],
}

/**
 * Which destination a path belongs to, or null.
 *
 * **Longest match wins**, so `/attendance/absences` resolves to Attendance
 * rather than to whatever else happens to start with `/attendance`. A path that
 * matches nothing gets no row, which is the honest answer for a screen nobody
 * has filed yet.
 */
export function destinationForPath(pathname: string): string | null {
  let best: { key: string; length: number } | null = null

  for (const [key, views] of Object.entries(VIEWS)) {
    for (const view of views) {
      const matches = view.end ? pathname === view.to : pathname.startsWith(view.to)

      if (matches && (!best || view.to.length > best.length)) {
        best = { key, length: view.to.length }
      }
    }
  }

  return best?.key ?? null
}
